/**
 * Notification event types + payload contract for the centralized
 * notification system. The DB stores `event_type` as a free-form string;
 * this enum is the canonical set producers should reference so renderers
 * stay aligned with what's actually written.
 *
 * Payload shapes are declared here too. Renderers are keyed by event type
 * and read fields off `payload` defensively — old DB rows may predate
 * payload-shape changes, so renderers should fall back gracefully on
 * missing fields rather than throw.
 */
export enum NotificationEventType {
  SystemAnnouncement = "system_announcement",
  InvoiceOverdue = "invoice_overdue",
  PaymentReceived = "payment_received",
  DiscountApprovalNeeded = "discount_approval_needed",
  SalesOrderApproved = "sales_order_approved",
  ReportReady = "report_ready",
  WorkflowFailure = "workflow_failure",
  WorkflowRerunLimitExhausted = "workflow_rerun_limit_exhausted",
  WorkflowVersionRolledBack = "workflow_version_rolled_back",
  WorkflowApprovalRequested = "workflow_approval_requested",
  WorkflowNotification = "workflow_notification",
  TaskReassigned = "task_reassigned",
  DraftSalesOrderCreated = "draft_sales_order_created",
  CollectionThreadAssigned = "collection_thread_assigned",
  CollectionThreadCommented = "collection_thread_commented",
  CollectionThreadStatusChanged = "collection_thread_status_changed",
  CollectionThreadOpened = "collection_thread_opened",
  AgentConversationReplyReceived = "agent_conversation_reply_received",
  AgentConversationNoResponse = "agent_conversation_no_response",
}

/**
 * Generic action button shape embedded in a notification payload under the
 * reserved `__action_payload__` key. Renderers detect this wrapper and
 * surface buttons inline; clicking dispatches to the named oRPC endpoint
 * with `actionPayload` parsed as JSON.
 */
export interface NotificationActionPayload {
  name: string;
  endpoint: string;
  actionPayload: string;
  buttonType: "primary" | "secondary";
}

export interface NotificationActionPayloadWrapper {
  __action_payload__: NotificationActionPayload[];
}

export const NOTIFICATION_ACTION_PAYLOAD_KEY = "__action_payload__";

export interface NotificationPayloadMap {
  [NotificationEventType.SystemAnnouncement]: {
    title: string;
    body: string;
    level?: "info" | "warn" | "critical";
  };
  [NotificationEventType.InvoiceOverdue]: {
    invoiceNumber: string;
    customerName: string;
    amount: number;
    daysOverdue: number;
  };
  [NotificationEventType.PaymentReceived]: {
    paymentNumber: string;
    customerName: string;
    amount: number;
  };
  [NotificationEventType.DiscountApprovalNeeded]: {
    discountId: string;
    customerName: string;
    discountPercent: number;
  };
  [NotificationEventType.SalesOrderApproved]: {
    salesOrderNumber: string;
    customerName: string;
  };
  [NotificationEventType.ReportReady]: {
    reportName: string;
    downloadUrl?: string;
  };
  [NotificationEventType.WorkflowFailure]: {
    workflowId: string;
    workflowName: string;
    runId: string;
    stepId: string | null;
    errorMessage: string;
  } & Partial<NotificationActionPayloadWrapper>;
  /**
   * A workflow stopped rerunning itself because the `snooze_workflow_rerun`
   * node's limit was reached. Terminal for that chain — nothing will retry it
   * automatically, so the owner has to act (hence the rerun action button).
   */
  [NotificationEventType.WorkflowRerunLimitExhausted]: {
    workflowId: string;
    workflowName: string;
    /** The original run the whole rerun chain traces back to. */
    rootRunId: string;
    /** The run whose snooze was refused. */
    lastRunId: string;
    limitReason: "max_reruns" | "expired";
    /** Which attempt was refused (1 = the first rerun). */
    attemptCount: number;
    /** The configured bound: `maxReruns`, or `expiresAfterMinutes`. */
    limit: number;
    /** Why the workflow was snoozing, e.g. "SO not ready". */
    reason: string;
  } & Partial<NotificationActionPayloadWrapper>;
  [NotificationEventType.WorkflowVersionRolledBack]: {
    workflowId: string;
    workflowName: string;
    runId: string;
    brokenVersionId: string;
    brokenVersionNumber: number;
    restoredVersionId: string;
    restoredVersionNumber: number;
    errorMessage: string;
  } & Partial<NotificationActionPayloadWrapper>;
  [NotificationEventType.WorkflowApprovalRequested]: {
    workflowName: string;
    workflowRunId: string;
    stepRunId: string;
    instructions: string;
  };
  /**
   * Generic informational notification raised by the `notify` workflow
   * step. Title/body are authored in the step config (with `{{var}}`
   * interpolation applied) — no decision or action is attached.
   */
  [NotificationEventType.WorkflowNotification]: {
    title: string;
    body: string;
  };
  [NotificationEventType.TaskReassigned]: {
    taskId: string;
    taskTitle: string;
    /** Reason supplied with the reassignment, if any. */
    reason?: string;
  };
  [NotificationEventType.DraftSalesOrderCreated]: {
    draftSalesOrderId: string;
    customerId: string;
    customerName?: string;
    purchaseOrderNumber?: string;
  };
  [NotificationEventType.CollectionThreadAssigned]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
  };
  [NotificationEventType.CollectionThreadCommented]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** Short preview of the comment body. */
    commentPreview?: string;
  };
  [NotificationEventType.CollectionThreadStatusChanged]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** New status after the change ("open" | "resolved"). */
    status: string;
    /** Resolution code when the thread was resolved. */
    resolution?: string;
  };
  [NotificationEventType.CollectionThreadOpened]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** True when opened by automated detection (e.g. short pay). */
    autoDetected?: boolean;
  };
  [NotificationEventType.AgentConversationReplyReceived]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** Subject of the customer reply that was filed. */
    replySubject?: string;
    /** The AI-suggested response draft awaiting review, if drafted. */
    draftId?: string | null;
  };
  [NotificationEventType.AgentConversationNoResponse]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** Subject of the unanswered outbound email. */
    subject?: string;
    /** Whole days elapsed since the send. */
    daysWaiting: number;
  };
}

export type NotificationPayload = NotificationPayloadMap[NotificationEventType];

/**
 * Recipient resolution strategies for NotificationService.create.
 *
 * Producers pass either an explicit user-id list (`recipientUserIds`) or
 * a strategy that the service resolves at send time:
 *   - `all_users`: every active user in the organization.
 *   - `user_groups`: every member of the listed UserGroup IDs.
 *   - `entity_watchers`: every CoreWatch row pointing at the given
 *     `notifiableType` + `notifiableId`, optionally filtered by
 *     `watchRoles`.
 *
 * All strategies resolve at send time, so newly-added users/groups are
 * picked up automatically without re-saving the workflow.
 */
export type NotificationRecipientStrategy =
  | "all_users"
  | "user_groups"
  | "entity_watchers";
